import React from 'react'

// MUI Imports
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Box,
  Tooltip
} from '@mui/material'
import {
  Reply,
  AttachFile,
  Description,
  ArrowDownward,
  ArrowUpward
} from '@mui/icons-material'

// Component Imports
import { useTranslation } from 'react-i18next'

// Styles Imports
import styles from '../styles/mailbox.module.scss'
import McPageHelpers from '../../../../pages/mc-components/McPageHelpers'
import { getInitials } from '../../utils/helpers'

const messageTypes = {
  OUTGOING: 'OUTGOING',
  INCOMING: 'INCOMING'
}

const MailCard = ({ data, isReplies, setRepliedMessage, compact = false }) => {
  const { t } = useTranslation()
  const userLocale = McPageHelpers.localeMap[localStorage.getItem('lang')] || 'en-US'

  const isIncoming = data?.type === messageTypes.INCOMING

  const directionLabel = isIncoming
    ? t('mailTemplate.mailbox.direction.received')
    : t('mailTemplate.mailbox.direction.sent')

  const DirectionIcon = isIncoming
    ? ArrowDownward
    : ArrowUpward

  const fromDisplay = data?.from_name || data?.from_address || ''
  const toDisplay = data?.to?.[0]?.name || data?.to?.[0]?.address || ''

  const formattedDate = data?.sentTimeMillis
    ? new Intl.DateTimeFormat(userLocale, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(data.sentTimeMillis))
    : ''

  const handleDownloadAttachment = (attachment) => {
    try {
      const base64Data = attachment.fileBytes
      const contentType = attachment.contentType || 'application/octet-stream'
      const fileName = attachment.fileName || 'attachment'

      const byteCharacters = atob(base64Data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: contentType })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading attachment:', error)
    }
  }

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2.5,
        overflow: 'hidden',
        ...(compact && { opacity: 0.85 })
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        px: 5,
        pt: 3,
        pb: 2,
        bgcolor: 'background.paper'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0, flex: 1 }}>
          {/* Avatar */}
          <Box sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: 'primary.50',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: '0.75rem',
            flexShrink: 0
          }}>
            {getInitials(fromDisplay)}
          </Box>

          {/* Sender info */}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'text.primary' }} noWrap>
                {fromDisplay}
              </Typography>
              <Chip
                size="small"
                icon={<DirectionIcon sx={{ fontSize: '0.7rem !important' }} />}
                label={directionLabel}
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  bgcolor: 'action.hover',
                  color: 'text.secondary',
                  '& .MuiChip-label': { px: 0.5 },
                  '& .MuiChip-icon': { ml: 0.5, mr: -0.25, color: 'text.disabled' }
                }}
              />
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }} noWrap>
              {t('mailTemplate.mailbox.list.to')}: {toDisplay}
            </Typography>
          </Box>
        </Box>

        {/* Right side: date + reply */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 1 }}>
          <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', whiteSpace: 'nowrap' }}>
            {formattedDate}
          </Typography>
          <Tooltip title={t('mailTemplate.mailbox.send.replyTitle')} arrow>
            <IconButton
              size="small"
              onClick={() => setRepliedMessage(data)}
              sx={{
                width: 30,
                height: 30,
                color: 'primary.main',
                '&:hover': { bgcolor: 'primary.50' }
              }}
            >
              <Reply sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Subject line */}
      {data?.subject && (
        <Box sx={{ px: 5, pb: 1.5 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: 'text.secondary', fontStyle: 'italic' }}>
            {data.subject}
          </Typography>
        </Box>
      )}

      {/* Body */}
      <CardContent sx={{ px: 5, pt: 0, pb: 4, '&:last-child': { pb: 4 } }}>
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
          <div
            className={styles.message}
            style={{ fontSize: '0.85rem', lineHeight: 1.7, color: 'var(--mui-palette-text-primary)' }}
            dangerouslySetInnerHTML={{ __html: data?.bodyHtml }}
          />
        </Box>

        {/* Attachments */}
        {data?.attachments?.length > 0 && (
          <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
              <AttachFile sx={{ fontSize: 16, color: 'text.secondary', transform: 'rotate(45deg)' }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
                {t('mailTemplate.mailbox.attachments', { count: data.attachments.length })}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {data.attachments.map((attachment, index) => {
                const fileName = attachment.fileName || attachment.name || `Attachment ${index + 1}`
                const fileSize = attachment.fileSize || attachment.size
                const fileSizeKB = fileSize ? Math.round(fileSize / 1024) : null
                const fileSizeDisplay = fileSizeKB
                  ? (fileSizeKB > 1024
                      ? `${(fileSizeKB / 1024).toFixed(1)} MB`
                      : `${fileSizeKB} KB`)
                  : null

                return (
                  <Chip
                    key={`${fileName}-${index}`}
                    icon={<Description sx={{ fontSize: '1rem !important' }} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                          {fileName}
                        </Typography>
                        {fileSizeDisplay && (
                          <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>
                            ({fileSizeDisplay})
                          </Typography>
                        )}
                      </Box>
                    }
                    variant='outlined'
                    onClick={() => handleDownloadAttachment(attachment)}
                    sx={{
                      height: 32,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      borderColor: 'divider',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        borderColor: 'primary.light'
                      }
                    }}
                  />
                )
              })}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default MailCard
